(ns minorodata.transformations.api
  (:require [mold-http.core :refer [reg-event-http]]
            [minorodata.transformations.model :refer [->transformations-view ->transformations-search]]
            [minorodata.group.model :refer [->group]]))


;; =============================================================================
;; Cyclic dependencies check
;; =============================================================================

(defn cyclic-deps-check-request [_ [{:keys [show-warnings on-cycle-ev]}]]
  {:http-xhrio
   {:url       "/api/cdc"
    :cb-params {:show-warnings (or show-warnings false)
                :on-cycle-ev   on-cycle-ev}}})

(defn cyclic-deps-check-callback [_ [error resp {:keys [show-warnings on-cycle-ev]}]]
  (if (some? error)
    {:dispatch [:app.api/log :error "Failed to check the cyclic dependencies" error]}

    (let [{:keys [has-cycles edges-in-cycles]} resp
          has-cycles? (or has-cycles false)
          events      (cond-> []
                        (and show-warnings has-cycles?)
                        (conj [:app.events/add-snack
                               :warning
                               "Warning! There are cyclic dependencies between transforms. These are highlighted in red on the Pipelines page."])

                        (and on-cycle-ev has-cycles?)
                        (conj on-cycle-ev)

                        :always
                        (conj [:transformations-view/add
                               (->transformations-view
                                {:has-cycles has-cycles?
                                 :edges      (set (flatten edges-in-cycles))})]))]
      {:dispatch-n events})))

(reg-event-http
  :transformations.api/cyclic-deps-check
  cyclic-deps-check-request
  cyclic-deps-check-callback)


;; =============================================================================
;; Fetch autocomplete options for SQL
;; =============================================================================

;; SQL editor format
(defn table-option-formatter [table]
  (let [{:keys [table-name table-cat]} table]
    {:caption table-name :value table-name :meta (str "table in " table-cat)}))


(defn column-option-formatter [column]
  (let [{:keys [column-name table-name]} column]
    {:caption column-name :value column-name :meta (str "column in " table-name)}))


(defn schema-option-formatter [schema]
  (let [{:keys [table-schem table-catalog]} schema]
    {:caption table-schem :value table-schem :meta (str "schema in " table-catalog)}))


(defn catalogs-option-formatter [catalog]
  (let [{:keys [table-cat]} catalog]
    {:caption table-cat :value table-cat :meta "table"}))


;; Completions
(defn completions-result [resp]
  (let [{:keys [tables columns schemas catalogs]} resp
        table-fns   (mapv table-option-formatter tables)
        column-fns  (mapv column-option-formatter columns)
        schema-fns  (mapv schema-option-formatter schemas)
        catalog-fns (mapv catalogs-option-formatter catalogs)]
    (concat table-fns column-fns schema-fns catalog-fns)))


(defn fetch-completions-request [_ _]
  {:http-xhrio
   {:id  :completions-fetch-request
    :url "/api/sink-metadata"}})

(defn fetch-completions-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.api/log :error "Failed to load autocomplete suggestions" error]}

    (let [completions (completions-result resp)]
      {:dispatch
       [:transformations-view/update {:transformations-view/sql-completions completions}]

       :app.events/add-ace-completer
       completions})))

(reg-event-http
  :transformations.api/fetch-completions
  fetch-completions-request
  fetch-completions-callback
  :cache? true)


;; =============================================================================
;; Search
;; =============================================================================

(defn load-search-request [_ [params]]
  {:http-xhrio
   {:id       :load-search-request
    :strategy :keep-latest
    :url      "/api/transformations/search"
    :method   :post
    :params   params}})

(defn load-search-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.api/log :error "Failed to complete search" error]}

    (let [{:keys [transforms groups parent-groups]} resp]
      {:dispatch-n [[:group/add-many (map ->group parent-groups)]
                    [:transformations-search/update
                     (->transformations-search
                       {:transforms (or (:data transforms) [])
                        :groups     (or (:data groups) [])})
                     {:replace true}]]})))

(reg-event-http
  :transformations.api/load-search-results
  load-search-request
  load-search-callback)


;; =============================================================================
;; Load Tags
;; =============================================================================

(defn fetch-tags-request [_ _]
  {:http-xhrio
   {:url "/api/transform-tags"}})

(defn fetch-tags-callback [_ [error resp]]
  (if (some? error)
    {:dispatch [:app.api/log :error "Failed to load tags" error]}
    {:dispatch [:transformations-view/add
                (->transformations-view {:transforms-tags (:all-tags resp)})]}))

(reg-event-http
  :transformations.api/fetch-tags
  fetch-tags-request
  fetch-tags-callback)


;; =============================================================================
;; Unit tests
;; =============================================================================

(defn check-unit-tests-request [_ [{:keys [on-failure on-success]}]]
  {:http-xhrio
   {:url "/api/transformations/unit-tests/status"
    :cb-params {:on-failure on-failure
                :on-success on-success}}})

(defn check-unit-tests-callback [_ [error resp {:keys [on-failure on-success]}]]
  (cond
    (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to check if unit tests can be created"]}

    (not (:ready resp))
    (on-failure)

    (:ready resp)
    (on-success)))

(reg-event-http
  :transformations.api/check-unit-tests
  check-unit-tests-request
  check-unit-tests-callback)


(defn create-procedures-for-unit-tests-request [_ [{:keys [on-success]}]]
  {:http-xhrio
   {:url "/api/transformations/unit-tests/setup"
    :cb-params {:on-success on-success}}})

(defn create-procedures-for-unit-tests-callback [_ [error resp {:keys [on-success]}]]
  (cond
    (some? error)
    {:dispatch [:app.events/add-snack :error "Failed to setup unit tests."]}

    (= (:status resp) "OK")
    (on-success)))

(reg-event-http
 :transformations.api/create-procedures-for-unit-tests
 create-procedures-for-unit-tests-request
 create-procedures-for-unit-tests-callback)
